import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import SeatMap from "@/components/SeatMap";
import POSLiveFeed from "@/components/POSLiveFeed";
import { Html5Qrcode } from "html5-qrcode";
import { QRCodeCanvas } from "qrcode.react";
import { Trash2, CheckCircle2, Camera, ScanLine, X, Printer, Receipt, CreditCard, Loader2, Monitor, Maximize2, Radio, PlugZap, Copy, ExternalLink, Lock, Users } from "lucide-react";
import { toast } from "sonner";
import POSLockOverlay from "@/components/POSLockOverlay";
import WaitlistEnroll from "@/components/WaitlistEnroll";
import { useAuth } from "@/context/AuthContext";
import useBarcodeScanner from "@/hooks/useBarcodeScanner";

const DISPLAY_KEY_STORE = "mh_pos_display_key";
const DEFAULT_TERMINALS = ["kasse-1", "kasse-2", "kasse-3", "kasse-4"];
const terminalLabel = (id) => {
  if (!id) return "—";
  const m = /^kasse-(\d+)$/i.exec(id);
  return m ? `Kasse ${m[1]}` : id;
};

const TICKET_TYPES = ["Standard", "Kind", "Senior", "VIP", "Einladung"];
const PAY = [
  { v: "bar", l: "Bar" }, { v: "karte", l: "Karte" },
  { v: "karte_stripe", l: "Karte · Stripe" },
  { v: "gutschein", l: "Gutschein" }, { v: "einladung", l: "Einladung" },
  { v: "frei", l: "Kostenlos" }, { v: "mitglied", l: "Mitgliedskarte" },
];

export default function POS() {
  const { user, logout } = useAuth();
  const [locked, setLocked] = useState(false);
  const [showtimes, setShowtimes] = useState([]);
  const [showtimeId, setShowtimeId] = useState("");
  const [showtime, setShowtime] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [ticketType, setTicketType] = useState("Standard");
  const [guestName, setGuestName] = useState("Kunde");
  const [guests, setGuests] = useState([]);
  const [guestId, setGuestId] = useState("");
  const [tierInfo, setTierInfo] = useState(null);
  const [cartProducts, setCartProducts] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [voucher, setVoucher] = useState("");
  const [voucherInfo, setVoucherInfo] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);  // {order, tickets}
  const [payment, setPayment] = useState("bar");
  const [busy, setBusy] = useState(false);
  const [stripeModal, setStripeModal] = useState(null); // {sessionId, url, amount, description}
  const [category, setCategory] = useState("Popcorn");
  const [displayKey, setDisplayKey] = useState(() => localStorage.getItem(DISPLAY_KEY_STORE) || "kasse-1");
  const [displayStatus, setDisplayStatus] = useState("off"); // off | connecting | live | closed
  const [displayPeers, setDisplayPeers] = useState(0);
  const [fullscreenPreview, setFullscreenPreview] = useState(false);
  const [thanksInfo, setThanksInfo] = useState(null); // {showtime, ticketsCount, orderNumber, guest, total, at}
  const [cashModal, setCashModal] = useState(null); // {total, given}
  const [posTemplates, setPosTemplates] = useState([]);
  const [splitMode, setSplitMode] = useState(false);
  const [splitPayments, setSplitPayments] = useState([{ method: "bar", amount: 0 }, { method: "karte", amount: 0 }]);
  const [reservationLookup, setReservationLookup] = useState("");
  const [pendingReservation, setPendingReservation] = useState(null);
  const [tip, setTip] = useState(0);
  const [familyInfo, setFamilyInfo] = useState({ min_tickets: 5, percent: 10, discount: 0 });
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const displayWsRef = useRef(null);

  useEffect(() => {
    api.get("/showtimes").then((r) => setShowtimes(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/guests").then((r) => setGuests(r.data));
  }, []);

  useEffect(() => {
    if (!guestId) { setTierInfo(null); return; }
    api.get(`/pos/tier-preview/${guestId}`).then((r) => {
      setTierInfo(r.data);
      setGuestName(r.data.guest_name || "Kunde");
    }).catch(() => setTierInfo(null));
  }, [guestId]);

  useEffect(() => {
    if (!showtimeId) return setShowtime(null);
    api.get(`/showtimes/${showtimeId}`).then((r) => setShowtime(r.data));
  }, [showtimeId]);

  const priceEach = useMemo(() => {
    const base = showtime?.ticket_price || 0;
    if (ticketType === "Kind") return +(base * 0.7).toFixed(2);
    if (ticketType === "Senior") return +(base * 0.85).toFixed(2);
    if (ticketType === "VIP") return +(base * 1.3).toFixed(2);
    if (ticketType === "Einladung") return 0;
    return base;
  }, [ticketType, showtime]);

  const snackSubtotal = useMemo(() => cartProducts.reduce((s, p) => s + p.quantity * p.unit_price, 0), [cartProducts]);
  const tierDiscount = useMemo(() => {
    if (!tierInfo?.is_member || !(tierInfo.discount_pct > 0) || snackSubtotal <= 0) return 0;
    return +(snackSubtotal * tierInfo.discount_pct).toFixed(2);
  }, [tierInfo, snackSubtotal]);

  // Abo-Perks: Snack-Rabatt + freie Sitze
  const aboSnackDiscount = useMemo(() => {
    const pct = Number(tierInfo?.abo?.snack_discount_pct || 0);
    if (!pct || snackSubtotal <= 0) return 0;
    return +(snackSubtotal * pct / 100).toFixed(2);
  }, [tierInfo, snackSubtotal]);
  const aboFreeSavings = useMemo(() => {
    const included = Number(tierInfo?.abo?.seats_included || 0);
    if (!included || selectedSeats.length === 0 || priceEach <= 0) return 0;
    const freeCount = Math.min(included, selectedSeats.length);
    return +(freeCount * priceEach).toFixed(2);
  }, [tierInfo, selectedSeats.length, priceEach]);

  const subtotal = useMemo(() => {
    const st = priceEach * selectedSeats.length;
    return +(st + snackSubtotal).toFixed(2);
  }, [priceEach, selectedSeats, snackSubtotal]);

  const total = useMemo(() => {
    // Einladung/Kostenlos → immer 0 €
    if (payment === "einladung" || payment === "frei") return 0;
    const tipVal = Number(tip) > 0 ? Number(tip) : 0;
    const fam = Number(familyInfo?.discount || 0);
    return Math.max(0, +(subtotal - (parseFloat(discount) || 0) - tierDiscount - aboSnackDiscount - aboFreeSavings - fam + tipVal).toFixed(2));
  }, [payment, subtotal, discount, tierDiscount, aboSnackDiscount, aboFreeSavings, tip, familyInfo]);

  // Familientarif automatisch abrufen (bei 5+ Sitzen)
  useEffect(() => {
    if (selectedSeats.length < 1 || priceEach <= 0) {
      setFamilyInfo((f) => ({ ...f, discount: 0 })); return;
    }
    const sub = priceEach * selectedSeats.length;
    api.get(`/pricing/family?ticket_count=${selectedSeats.length}&subtotal=${sub}`)
      .then(({ data }) => setFamilyInfo(data)).catch(() => {});
  }, [selectedSeats.length, priceEach]);

  const toggleSeat = (id) => {
    setSelectedSeats((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };

  const addProd = (p) => {
    setCartProducts((cp) => {
      const ex = cp.find((x) => x.product_id === p.id);
      if (ex) return cp.map((x) => x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x);
      return [...cp, { product_id: p.id, name: p.name, quantity: 1, unit_price: p.sell_price }];
    });
  };

  const changeQty = (pid, d) => setCartProducts((cp) => cp.map((x) => x.product_id === pid ? { ...x, quantity: Math.max(1, x.quantity + d) } : x));
  const removeProd = (pid) => setCartProducts((cp) => cp.filter((x) => x.product_id !== pid));

  // --- USB-HID Barcode-Scanner --------------------------------------------
  // Erkennt fast getippte Zeichenfolgen (Enter am Ende) und löst sie universell auf:
  // Produkt → in Warenkorb · Voucher → Feld füllen + Guthaben ziehen
  // Ticket → info (später Einlass-Check) · Mitglied → Gast auswählen
  const [lastScan, setLastScan] = useState(null);  // {kind, at, label}
  useBarcodeScanner(async (code) => {
    try {
      const { data } = await api.get(`/pos/scan/${encodeURIComponent(code)}`);
      if (data.kind === "product") {
        const p = data.product;
        addProd({ id: p.id, name: p.name, sell_price: p.sell_price });
        toast.success(`+ ${p.name} (${(p.sell_price || 0).toFixed(2)} €)`);
        setLastScan({ kind: "product", at: Date.now(), label: p.name });
      } else if (data.kind === "voucher") {
        const v = data.voucher;
        setVoucher(v.code); setVoucherInfo(v);
        if (v.status !== "aktiv") toast.warning(`Gutschein ${v.status}`);
        else toast.success(`Gutschein ${v.code} · ${(v.remaining_value || 0).toFixed(2)} €`);
        setLastScan({ kind: "voucher", at: Date.now(), label: `${v.code} · ${(v.remaining_value || 0).toFixed(2)} €` });
      } else if (data.kind === "member") {
        const g = data.guest;
        setGuestId(g.id);
        toast.success(`Mitglied · ${g.first_name} ${g.last_name}`);
        setLastScan({ kind: "member", at: Date.now(), label: `${g.first_name} ${g.last_name}` });
      } else if (data.kind === "ticket") {
        const t = data.ticket;
        toast.info(`Ticket · ${t.row}${t.seat} · Status: ${t.status}`);
        setLastScan({ kind: "ticket", at: Date.now(), label: `${t.row}${t.seat} · ${t.status}` });
      } else if (data.kind === "snack_order") {
        toast.info(`Snack-Bestellung #${data.order.number} · ${data.order.status}`);
        setLastScan({ kind: "snack_order", at: Date.now(), label: `#${data.order.number}` });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Scan „${code}" nicht erkannt`);
    }
  });

  const cats = ["Popcorn", "Nachos", "Getränke", "Süßigkeiten", "Menüs", "Extras"];

  // Schnellverkauf-Templates (1-Klick-Kombis) laden
  useEffect(() => {
    api.get("/pos-templates/active").then((r) => setPosTemplates(r.data || [])).catch(() => setPosTemplates([]));
  }, []);

  const applyTemplate = (tpl) => {
    // Produkte hinzufügen (kumulativ)
    if (tpl.products?.length) {
      setCartProducts((cp) => {
        const next = [...cp];
        for (const item of tpl.products) {
          const prod = products.find((x) => x.id === item.product_id);
          if (!prod) continue;
          const existing = next.find((n) => n.product_id === prod.id);
          if (existing) existing.quantity += Number(item.quantity || 1);
          else next.push({ product_id: prod.id, name: prod.name, quantity: Number(item.quantity || 1), unit_price: prod.sell_price });
        }
        return next;
      });
    }
    // Rabatt anwenden bei fixem Kombi-Preis (Differenz zwischen normal & override)
    if (tpl.price_override != null) {
      // wir bewerten Differenz als „Kombi-Rabatt" — Zwischen­summe wird bei Verkauf validiert
      const normal = (tpl.products || []).reduce((s, it) => {
        const p = products.find((x) => x.id === it.product_id);
        return s + (p ? p.sell_price * it.quantity : 0);
      }, 0) + (tpl.ticket_count || 0) * priceEach;
      const diff = normal - Number(tpl.price_override);
      if (diff > 0) setDiscount(((parseFloat(discount) || 0) + diff).toFixed(2));
    }
    if (tpl.ticket_type) setTicketType(tpl.ticket_type);
    toast.success(`Kombi „${tpl.name}" hinzugefügt`);
  };

  // Reservierung nachladen (für Rest-Zahlung)
  const loadReservation = async () => {
    const code = reservationLookup.trim();
    if (!code) return;
    try {
      const { data } = await api.get(`/reservations/${code}`);
      setPendingReservation(data);
      toast.success("Reservierung geladen");
    } catch {
      toast.error("Reservierung nicht gefunden");
    }
  };

  const payReservationRemaining = async () => {
    if (!pendingReservation) return;
    setBusy(true);
    try {
      const body = { payment_method: payment };
      if (splitMode) body.payments = splitPayments.filter((p) => Number(p.amount) > 0);
      const { data } = await api.post(`/reservations/${pendingReservation.id}/pay-remaining`, body);
      toast.success(`Restzahlung ${data.remaining_paid.toFixed(2)} € kassiert`);
      setPendingReservation(null); setReservationLookup("");
      setSplitMode(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler bei Restzahlung");
    } finally { setBusy(false); }
  };

  const payReservationDeposit = async () => {
    if (!pendingReservation) return;
    const outstanding = Math.max(0, Number(pendingReservation.deposit_amount || 0) - Number(pendingReservation.deposit_paid || 0));
    if (outstanding <= 0) return toast.error("Keine offene Anzahlung");
    setBusy(true);
    try {
      const { data } = await api.post(`/reservations/${pendingReservation.id}/pay-deposit`, { payment_method: payment });
      toast.success(`Anzahlung ${outstanding.toFixed(2)} € kassiert · Beleg ${data.order.order_number}`);
      // Reservierung aus dem POS-Kontext neu laden
      try {
        const { data: r } = await api.get(`/reservations/${pendingReservation.id}`);
        setPendingReservation(r);
      } catch { /* noop */ }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler bei Anzahlung");
    } finally { setBusy(false); }
  };

  const addSplitLine = () => setSplitPayments((sp) => [...sp, { method: "bar", amount: 0 }]);
  const removeSplitLine = (idx) => setSplitPayments((sp) => sp.filter((_, i) => i !== idx));
  const updateSplitLine = (idx, patch) => setSplitPayments((sp) => sp.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const splitSum = splitPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const splitBalanced = Math.abs(splitSum - total) < 0.01 && splitPayments.filter((p) => Number(p.amount) > 0).length >= 2;


  const displayUrl = displayKey
    ? `${window.location.origin}/display/pos/${displayKey}`
    : "";

  const changeTerminal = (id) => {
    const cleaned = String(id || "").trim().toLowerCase().replace(/\s+/g, "-");
    if (!cleaned) return;
    // Trenne alte Session sauber
    const ws = displayWsRef.current;
    if (ws) { try { ws.send(JSON.stringify({ type: "close" })); } catch { /* noop */ } try { ws.close(); } catch { /* noop */ } }
    displayWsRef.current = null;
    localStorage.setItem(DISPLAY_KEY_STORE, cleaned);
    setDisplayKey(cleaned);
    setDisplayStatus("connecting");
    setDisplayPeers(0);
  };

  const openSecondScreen = () => {
    if (!displayKey) return toast.error("Bitte Kassen-Terminal wählen");
    const url = `/display/pos/${displayKey}`;
    const win = window.open(url, `mh_display_${displayKey}`, "noopener,noreferrer");
    if (!win) toast.error("Popup blockiert — bitte erlauben");
    else toast.success(`Kunden-Display ${terminalLabel(displayKey)} geöffnet`);
  };

  const copyDisplayUrl = async () => {
    if (!displayUrl) return;
    try {
      await navigator.clipboard.writeText(displayUrl);
      toast.success("Link kopiert");
    } catch {
      toast.error("Konnte nicht kopieren");
    }
  };

  const closeSecondScreen = () => {
    const ws = displayWsRef.current;
    if (ws) { try { ws.send(JSON.stringify({ type: "close" })); } catch { /* noop */ } }
    if (ws) { try { ws.close(); } catch { /* noop */ } }
    displayWsRef.current = null;
    setDisplayStatus("off");
    setDisplayPeers(0);
    toast.info("Kunden-Display getrennt");
  };

  // Publisher-WS: läuft nur wenn displayKey gesetzt ist
  useEffect(() => {
    if (!displayKey) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = base.replace(/^https/i, "wss").replace(/^http/i, "ws") + `/api/pos/display/${displayKey}`;
    let ws, hb, reconnect, closed = false;
    const connect = () => {
      setDisplayStatus("connecting");
      try { ws = new WebSocket(url); } catch { setDisplayStatus("closed"); return; }
      displayWsRef.current = ws;
      ws.onopen = () => {
        setDisplayStatus("live");
        hb = setInterval(() => { try { ws.send("ping"); } catch { /* noop */ } }, 25000);
      };
      ws.onmessage = (m) => {
        try {
          const ev = JSON.parse(m.data);
          if (ev.type === "hello" || ev.type === "peers") setDisplayPeers(ev.peers || 0);
        } catch { /* noop */ }
      };
      ws.onclose = () => {
        setDisplayStatus("closed");
        if (hb) clearInterval(hb);
        if (!closed) reconnect = setTimeout(connect, 2500);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => {
      closed = true;
      if (hb) clearInterval(hb);
      if (reconnect) clearTimeout(reconnect);
      if (ws) { try { ws.close(); } catch { /* noop */ } }
      displayWsRef.current = null;
    };
  }, [displayKey]);

  // State-Publisher: bei jeder relevanten Änderung → an alle Peers pushen.
  // Priorität: stripe_qr (aktive Zahlung) > thanks (nach Verkauf) > normal.
  useEffect(() => {
    const ws = displayWsRef.current;
    if (!ws || displayStatus !== "live") return;
    const baseShowtime = showtime ? {
      id: showtime.id,
      date: showtime.date,
      start_time: showtime.start_time,
      hall: showtime.hall,
      seat_plan: showtime.seat_plan,
      seat_status: showtime.seat_status,
      movie: showtime.movie ? {
        title_de: showtime.movie.title_de,
        title_original: showtime.movie.title_original,
        poster_url: showtime.movie.poster_url,
        duration_min: showtime.movie.duration_min,
        age_rating: showtime.movie.age_rating,
      } : null,
    } : null;
    let payload;
    if (stripeModal) {
      payload = {
        type: "state",
        state: {
          mode: "stripe_qr",
          stripeUrl: stripeModal.url,
          amount: stripeModal.amount,
          description: stripeModal.description,
          showtime: baseShowtime,
          selected: selectedSeats,
          ticketType,
          products: cartProducts,
          total,
          guest: guestName,
        },
      };
    } else if (thanksInfo) {
      payload = {
        type: "state",
        state: {
          mode: "thanks",
          showtime: thanksInfo.showtime,
          ticketsCount: thanksInfo.ticketsCount,
          orderNumber: thanksInfo.orderNumber,
          guest: thanksInfo.guest,
          total: thanksInfo.total,
          at: thanksInfo.at,
        },
      };
    } else {
      payload = {
        type: "state",
        state: {
          showtime: baseShowtime,
          selected: selectedSeats,
          ticketType,
          priceEach,
          products: cartProducts,
          subtotal,
          tierDiscount,
          discount: parseFloat(discount) || 0,
          voucherDeducted: 0,
          voucherInfo: voucherInfo ? { kind: voucherInfo.kind, remaining: voucherInfo.remaining_value, code: voucher } : null,
          total,
          payment,
          guest: guestName,
          tierInfo,
        },
      };
    }
    try { ws.send(JSON.stringify(payload)); } catch { /* noop */ }
  }, [displayStatus, stripeModal, thanksInfo, showtime, selectedSeats, ticketType, priceEach, cartProducts, subtotal, tierDiscount, discount, voucherInfo, total, payment, guestName, tierInfo]);

  // Auto-Reset des Danke-Bildschirms nach 15 s zurück in Idle (Logo + Poster-Karussell)
  useEffect(() => {
    if (!thanksInfo) return;
    const t = setTimeout(() => setThanksInfo(null), 15000);
    return () => clearTimeout(t);
  }, [thanksInfo]);

  const triggerDankeDisplay = (data) => {
    // Wird nach erfolgreichem Verkauf aufgerufen, damit der 2. Bildschirm
    // groß „Danke — Ihre Tickets werden gedruckt" mit Poster + Countdown zeigt.
    if (!showtime && !data?.order) return;
    setThanksInfo({
      showtime: showtime ? {
        date: showtime.date,
        start_time: showtime.start_time,
        hall: showtime.hall,
        movie: showtime.movie ? {
          title_de: showtime.movie.title_de,
          title_original: showtime.movie.title_original,
          poster_url: showtime.movie.poster_url,
          duration_min: showtime.movie.duration_min,
          age_rating: showtime.movie.age_rating,
        } : null,
      } : null,
      ticketsCount: data.tickets?.length || 0,
      orderNumber: data.order?.order_number || "",
      guest: guestName,
      total: data.order?.total ?? total,
      at: Date.now(),
    });
  };

  const sell = async () => {
    if (!showtimeId && selectedSeats.length) return toast.error("Bitte Vorstellung wählen");
    if (splitMode && !splitBalanced) return toast.error(`Split-Zahlarten (${splitSum.toFixed(2)} €) müssen dem Gesamt (${total.toFixed(2)} €) entsprechen`);
    if (!splitMode && payment === "karte_stripe") return openStripePay();
    const hasCash = splitMode
      ? splitPayments.some((p) => p.method === "bar" && Number(p.amount) > 0)
      : (payment === "bar" && total > 0);
    if (!splitMode && payment === "bar" && total > 0 && !cashModal) {
      // Wechselgeld-Rechner: erst Betrag eingeben, dann verkaufen
      setCashModal({ total, given: total });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        showtime_id: showtimeId || null,
        seats: selectedSeats,
        ticket_type: ticketType,
        guest_id: guestId || null,
        guest_name: guestName,
        products: cartProducts,
        discount: parseFloat(discount) || 0,
        voucher_code: voucher || null,
        payment_method: splitMode ? "split" : payment,
        tip: Number(tip) > 0 ? Number(tip) : 0,
      };
      if (splitMode) {
        payload.payments = splitPayments
          .filter((p) => Number(p.amount) > 0)
          .map((p) => ({ method: p.method, amount: Number(p.amount) }));
      }
      const { data } = await api.post("/pos/sell", payload);
      // Auto-Schublade nur wenn Bar-Anteil vorhanden
      if (hasCash) {
        api.post("/pos/drawer-open", { printer_type: "receipt" }).catch(() => {/* still ok */});
      }
      toast.success(`Bestellung ${data.order.order_number} abgeschlossen`);
      triggerDankeDisplay(data);
      // Beleg-Popup zum Nachdrucken
      setReceipt({
        order: data.order,
        tickets: data.tickets || [],
        showtime,
        cartSnapshot: {
          seats: selectedSeats.map((s) => ({ id: s, price: priceEach, type: ticketType })),
          products: [...cartProducts],
          subtotal,
          discount: parseFloat(discount) || 0,
          tierDiscount,
          voucher: voucherInfo ? { code: voucher, kind: voucherInfo.kind } : null,
          voucherDeducted: (data.order?.voucher?.deducted) || 0,
          total: data.order?.total ?? total,
          payment: splitMode ? "split" : payment,
          payments: splitMode ? payload.payments : null,
          guest: guestName,
          cinema: "The Movie House",
        },
      });
      // reset
      setSelectedSeats([]); setCartProducts([]); setDiscount(0); setVoucher(""); setVoucherInfo(null);
      setTip(0);
      setSplitMode(false); setSplitPayments([{ method: "bar", amount: 0 }, { method: "karte", amount: 0 }]);
      if (showtimeId) api.get(`/showtimes/${showtimeId}`).then((r) => setShowtime(r.data));
      // open PDF for first ticket
      if (data.tickets?.[0]) {
        openPdf(`/tickets/${data.tickets[0].id}/pdf`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const openStripePay = async () => {
    if (total <= 0) return toast.error("Betrag muss > 0 sein");
    setBusy(true);
    try {
      const { data } = await api.post("/pos/stripe-session", {
        amount: total,
        description: showtime?.movie?.title_de ? `${showtime.movie.title_de} · ${selectedSeats.length} Ticket(s)` : "Kinokasse · Snacks/Getränke",
      });
      setStripeModal({ sessionId: data.session_id, url: data.checkout_url, amount: data.amount, description: "Kunde zahlt am Handy" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Stripe-Session fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const finalizeStripe = async () => {
    if (!stripeModal) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pos/sell", {
        showtime_id: showtimeId || null,
        seats: selectedSeats,
        ticket_type: ticketType,
        guest_id: guestId || null,
        guest_name: guestName,
        products: cartProducts,
        discount: parseFloat(discount) || 0,
        voucher_code: voucher || null,
        payment_method: "karte_stripe",
      });
      toast.success(`Bestellung ${data.order.order_number} · Stripe bezahlt`);
      triggerDankeDisplay(data);
      setReceipt({
        order: data.order, tickets: data.tickets || [], showtime,
        cartSnapshot: {
          seats: selectedSeats.map((s) => ({ id: s, price: priceEach, type: ticketType })),
          products: [...cartProducts], subtotal, discount: parseFloat(discount) || 0, tierDiscount,
          voucher: voucherInfo ? { code: voucher, kind: voucherInfo.kind } : null,
          voucherDeducted: (data.order?.voucher?.deducted) || 0,
          total: data.order?.total ?? total, payment: "karte_stripe",
          guest: guestName, cinema: "The Movie House",
        },
      });
      setSelectedSeats([]); setCartProducts([]); setDiscount(0); setVoucher(""); setVoucherInfo(null);
      setStripeModal(null);
      if (showtimeId) api.get(`/showtimes/${showtimeId}`).then((r) => setShowtime(r.data));
      if (data.tickets?.[0]) {
        openPdf(`/tickets/${data.tickets[0].id}/pdf`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Auto-Lock nach 10 Minuten Inaktivität
  useEffect(() => {
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setLocked(true), 10 * 60 * 1000);
    };
    const evts = ["mousemove", "keydown", "click", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      evts.forEach((e) => window.removeEventListener(e, reset));
    };
  }, []);

  return (
    <div className="grid xl:grid-cols-[1fr_420px] gap-6" data-testid="pos-page">
      <POSLockOverlay open={locked} cashierName={user?.name || user?.email || ""}
        onUnlock={() => setLocked(false)}
        onLogout={() => { logout?.(); setLocked(false); window.location.assign("/login"); }} />
      <POSLiveFeed />
      <div className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Verkauf</div>
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Kinokasse</h1>
            {user?.name && <div className="text-[10px] uppercase tracking-widest text-[#575148] mt-1" data-testid="pos-current-cashier">Kassierer: <span className="text-[#EEDC82]">{user.name}</span></div>}
          </div>
          <button onClick={() => setLocked(true)} data-testid="pos-lock-btn"
            className="border border-[#D4AF37]/50 text-[#D4AF37] px-3 py-2 rounded text-[11px] uppercase tracking-widest hover:bg-[#D4AF37]/10 inline-flex items-center gap-2">
            <Lock size={13} /> Kasse sperren
          </button>
        </div>

        {/* Warteliste-Einschreibung (POS-Parität mit Online-Buchung) */}
        {showtimeId && showtime && (
          <div className="mb-4">
            <button onClick={() => setWaitlistOpen((o) => !o)} data-testid="pos-waitlist-toggle"
              className="w-full text-left inline-flex items-center gap-2 border border-[#EEDC82]/40 text-[#EEDC82] px-3 py-2 rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82]/10">
              <Users size={13} /> {waitlistOpen ? "Warteliste ausblenden" : "Gast auf Warteliste setzen (bei Ausverkauft)"}
            </button>
            {waitlistOpen && (
              <div className="mt-2">
                <WaitlistEnroll showtimeId={showtimeId} showAsButton={false}
                  movieTitle={showtime?.movie?.title_de || showtime?.movie_title || "Vorstellung"}
                  dateStr={`${showtime?.date || ""} · ${showtime?.start_time || ""}`}
                  onEnrolled={() => setWaitlistOpen(false)} />
              </div>
            )}
          </div>
        )}

        <div className="card-lux p-4">
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Vorstellung</label>
          <select data-testid="pos-showtime-select" value={showtimeId} onChange={(e) => { setShowtimeId(e.target.value); setSelectedSeats([]); }}
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
            <option value="">— nur Snacks —</option>
            {showtimes.map((s) => <option key={s.id} value={s.id}>{s.date} {s.start_time} · {s.movie?.title_de || "?"} · {s.hall}</option>)}
          </select>
        </div>

        {/* Kassen-Terminal / 2. Bildschirm Setup */}
        <div className="card-lux p-4" data-testid="pos-terminal-card">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1.5">
                <Monitor size={11} className="text-[#D4AF37]" /> Kassen-Terminal · 2. Bildschirm
              </label>
              <div className="text-[10px] text-[#575148] mt-0.5">
                Wähle diese Kasse — der Link bleibt konstant, damit der 2. Monitor / Tablet immer mit derselben URL verbunden bleibt.
              </div>
            </div>
            <div className={`inline-flex items-center gap-1.5 border rounded-full px-2 py-0.5 text-[9px] uppercase tracking-widest ${displayStatus === "live" ? "border-[#D4AF37]/60 text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] text-[#A8A398]"}`}>
              <Radio size={9} className={displayStatus === "live" ? "animate-pulse" : ""} />
              {displayStatus === "live" ? `Display · ${Math.max(0, displayPeers - 1)}` : displayStatus === "connecting" ? "Verbinde …" : "Getrennt"}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {DEFAULT_TERMINALS.map((t) => (
              <button key={t} onClick={() => changeTerminal(t)} data-testid={`pos-terminal-${t}`}
                className={`text-xs px-3 py-1.5 rounded border uppercase tracking-widest transitions-safe ${displayKey === t ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]"}`}>
                {terminalLabel(t)}
              </button>
            ))}
            <input
              type="text"
              placeholder="Eigener Name …"
              value={DEFAULT_TERMINALS.includes(displayKey) ? "" : displayKey}
              onChange={(e) => changeTerminal(e.target.value)}
              data-testid="pos-terminal-custom"
              className="text-xs bg-[#050505] border border-[#2A2A2A] rounded px-3 py-1.5 focus:border-[#D4AF37] outline-none w-40"
            />
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <div className="flex-1 min-w-[200px] bg-[#050505] border border-[#1A1A1A] rounded px-2 py-1.5 font-mono text-xs text-[#EEDC82] truncate" data-testid="pos-display-url">
              {displayUrl}
            </div>
            <button onClick={copyDisplayUrl} data-testid="pos-display-url-copy"
              className="text-xs border border-[#D4AF37]/40 text-[#D4AF37] rounded px-3 py-1.5 hover:bg-[#D4AF37]/10 inline-flex items-center gap-1.5">
              <Copy size={12} /> Link
            </button>
            <button onClick={openSecondScreen} data-testid="pos-open-2nd-screen-btn"
              className="text-xs border border-[#D4AF37] bg-[#D4AF37] text-black rounded px-3 py-1.5 hover:bg-[#EEDC82] inline-flex items-center gap-1.5">
              <ExternalLink size={12} /> Fenster öffnen
            </button>
            {displayStatus === "live" && (
              <button onClick={closeSecondScreen} data-testid="pos-close-2nd-screen-btn"
                className="text-xs border border-[#7A1921]/60 text-[#EE9CA1] rounded px-3 py-1.5 hover:bg-[#7A1921]/20 inline-flex items-center gap-1.5">
                <PlugZap size={12} /> Trennen
              </button>
            )}
          </div>
        </div>

        {/* Schnellverkauf-Templates (1-Klick-Kombis) */}
        {posTemplates.length > 0 && (
          <div className="card-lux p-4" data-testid="pos-templates-card">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Schnellverkauf</label>
              <div className="text-[10px] text-[#575148]">— 1-Klick-Kombis</div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {posTemplates.map((tpl) => (
                <button key={tpl.id} onClick={() => applyTemplate(tpl)} data-testid={`pos-template-${tpl.id}`}
                  className="px-3 py-2 rounded border text-xs transitions-safe hover:bg-[#D4AF37]/10"
                  style={{ borderColor: (tpl.color || "#D4AF37") + "80", color: tpl.color || "#D4AF37" }}
                  title={tpl.description || ""}>
                  <div className="font-heading text-sm">{tpl.name}</div>
                  {tpl.price_override != null && (
                    <div className="text-[10px] opacity-70 font-mono">{Number(tpl.price_override).toFixed(2)} €</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Rest-Zahlung einer Reservierung */}
        <div className="card-lux p-4" data-testid="pos-remaining-card">
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Anzahlung · Restzahlung</label>
          <div className="flex gap-1.5 mt-1.5">
            <input type="text" value={reservationLookup} onChange={(e) => setReservationLookup(e.target.value)}
              placeholder="Reservierungs-ID eingeben …" data-testid="pos-remaining-input"
              className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
            <button onClick={loadReservation} data-testid="pos-remaining-lookup"
              className="text-xs px-3 py-2 border border-[#D4AF37]/60 text-[#D4AF37] rounded hover:bg-[#D4AF37]/10 uppercase tracking-widest">Suchen</button>
          </div>
          {pendingReservation && (
            <div className="mt-3 p-3 bg-[#0F0F0F] border border-[#D4AF37]/30 rounded">
              <div className="text-sm">{pendingReservation.guest_name} · {pendingReservation.ticket_ids?.length || 0} Sitz(e)</div>
              <div className="text-xs text-[#A8A398] mt-1 font-mono">
                Total: {Number(pendingReservation.total_price || 0).toFixed(2)} € ·
                Anzahlung {Number(pendingReservation.deposit_amount || 0) > 0 && (
                  <>vereinbart: <span className="text-[#EEDC82]">{Number(pendingReservation.deposit_amount || 0).toFixed(2)} €</span> · </>
                )}
                bezahlt: {Number(pendingReservation.deposit_paid || 0).toFixed(2)} € ·
                Rest: <span className="text-[#D4AF37] font-bold">{Number(pendingReservation.remaining_due || Math.max(0, (pendingReservation.total_price || 0) - (pendingReservation.deposit_paid || 0))).toFixed(2)} €</span>
              </div>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {Number(pendingReservation.deposit_amount || 0) > Number(pendingReservation.deposit_paid || 0) && (
                  <button onClick={payReservationDeposit} disabled={busy} data-testid="pos-deposit-pay"
                    className="flex-1 min-w-[140px] text-xs py-2 bg-[#EEDC82] hover:bg-[#F5E9A3] text-black rounded uppercase tracking-widest disabled:opacity-40">
                    Anzahlung kassieren ({(Number(pendingReservation.deposit_amount || 0) - Number(pendingReservation.deposit_paid || 0)).toFixed(2)} €)
                  </button>
                )}
                <button onClick={payReservationRemaining} disabled={busy} data-testid="pos-remaining-pay"
                  className="flex-1 min-w-[140px] text-xs py-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded uppercase tracking-widest disabled:opacity-40">
                  Rest kassieren ({(pendingReservation.remaining_due || Math.max(0, (pendingReservation.total_price || 0) - (pendingReservation.deposit_paid || 0))).toFixed(2)} €)
                </button>
                <button onClick={() => { setPendingReservation(null); setReservationLookup(""); }}
                  className="text-xs px-3 py-2 border border-[#2A2A2A] text-[#A8A398] rounded hover:border-[#7A1921]">×</button>
              </div>
            </div>
          )}
        </div>

        {showtime && (
          <div className="card-lux p-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-heading text-2xl">Sitzplatzauswahl</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <button data-testid="pos-fullscreen-preview-btn" onClick={() => setFullscreenPreview(true)}
                  className="text-xs border border-[#D4AF37]/40 text-[#D4AF37] rounded px-3 py-1.5 hover:bg-[#D4AF37]/10 inline-flex items-center gap-1.5 transitions-safe"
                  title="Vollbild-Sitzplan-Vorschau (auch für Beamer / großen Monitor)">
                  <Maximize2 size={13} /> Vollbild
                </button>
                <div className="text-xs text-[#A8A398] pl-2 border-l border-[#1A1A1A]">
                  {selectedSeats.length} Platz(e) · {(priceEach * selectedSeats.length).toFixed(2)} €
                </div>
              </div>
            </div>
            <SeatMap rows={showtime.seat_plan?.rows || []} seatTypes={showtime.seat_plan?.seat_types || []} seatStatus={showtime.seat_status || {}} selected={selectedSeats} onToggle={toggleSeat}
              decorations={showtime.seat_plan?.decorations || []} backgroundUrl={showtime.seat_plan?.background_url || ""} backgroundOpacity={showtime.seat_plan?.background_opacity ?? 0.35}
              layers={showtime.seat_plan?.layers || []} freeMode={!!showtime.seat_plan?.free_mode} curve={showtime.seat_plan?.default_curve ?? 8} />
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Ticketart</label>
                <select value={ticketType} onChange={(e) => setTicketType(e.target.value)}
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
                  {TICKET_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Gast (aus Club)</label>
                <select value={guestId} onChange={(e) => setGuestId(e.target.value)} data-testid="pos-guest-select"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
                  <option value="">— spontaner Gast —</option>
                  {guests.map((g) => (
                    <option key={g.id} value={g.id}>{g.first_name} {g.last_name}{g.is_member ? ` · ${(g.tier||"bronze").toUpperCase()}` : ""}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Gastname</label>
                <input value={guestName} onChange={(e) => setGuestName(e.target.value)} data-testid="pos-guest-name"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
              </div>
            </div>
          </div>
        )}

        <div className="card-lux p-6">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
            <h3 className="font-heading text-2xl">Produkte & Snacks</h3>
            <div className="flex gap-1 overflow-x-auto no-scrollbar">
              {cats.map((c) => (
                <button key={c} onClick={() => setCategory(c)}
                  className={`text-xs px-3 py-1 rounded uppercase tracking-widest border ${category === c ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398]"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {products.filter((p) => p.category === category && p.available !== false).map((p) => (
              <button key={p.id} data-testid={`pos-add-product-${p.id}`} onClick={() => addProd(p)}
                className="card-lux p-3 text-left hover:border-[#D4AF37] transitions-safe">
                <div className="text-sm">{p.name}</div>
                <div className="text-xs text-[#A8A398]">{p.sell_price?.toFixed(2)} €</div>
                <div className="text-[10px] text-[#A8A398] mt-1">Best.: {p.stock}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <aside className="card-lux p-6 sticky top-4 h-fit" data-testid="pos-cart">
        <h3 className="font-heading text-2xl mb-4">Warenkorb</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {selectedSeats.map((s) => (
            <div key={s} className="flex items-center justify-between text-sm border-b border-[#1A1A1A] py-2">
              <div><div>Ticket {ticketType}</div><div className="text-xs text-[#A8A398] font-mono">Platz {s}</div></div>
              <div className="font-mono">{priceEach.toFixed(2)} €</div>
            </div>
          ))}
          {cartProducts.map((p) => (
            <div key={p.product_id} className="flex items-center justify-between text-sm border-b border-[#1A1A1A] py-2">
              <div>
                <div>{p.name}</div>
                <div className="text-xs text-[#A8A398] font-mono flex items-center gap-2 mt-1">
                  <button onClick={() => changeQty(p.product_id, -1)} className="w-6 h-6 border border-[#2A2A2A] rounded">−</button>
                  {p.quantity}
                  <button onClick={() => changeQty(p.product_id, 1)} className="w-6 h-6 border border-[#2A2A2A] rounded">+</button>
                  <button onClick={() => removeProd(p.product_id)} className="text-[#EE9CA1]"><Trash2 size={12} /></button>
                </div>
              </div>
              <div className="font-mono">{(p.quantity * p.unit_price).toFixed(2)} €</div>
            </div>
          ))}
          {!selectedSeats.length && !cartProducts.length && <div className="text-[#A8A398] text-sm">Warenkorb leer</div>}
        </div>
        <div className="mt-4 space-y-2 text-sm">
          {tierInfo?.is_member && tierInfo.discount_pct > 0 && (
            <div className="rounded border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-3 py-2 text-xs" data-testid="pos-tier-badge">
              <div className="text-[#D4AF37] uppercase tracking-widest">{tierInfo.tier.toUpperCase()} Mitgliedsrabatt</div>
              <div className="text-[#A8A398] mt-0.5">{Math.round(tierInfo.discount_pct * 100)}% auf Snacks · {tierDiscount.toFixed(2)} €</div>
            </div>
          )}
          {tierInfo?.abo && (
            <div className="rounded border border-[#8FBC8F]/40 bg-[#8FBC8F]/5 px-3 py-2 text-xs" data-testid="pos-abo-badge">
              <div className="text-[#8FBC8F] uppercase tracking-widest flex items-center gap-2">
                Abo aktiv · {tierInfo.abo.label}
                <span className="text-[9px] bg-[#8FBC8F]/20 px-1.5 py-0.5 rounded">Auto</span>
              </div>
              <div className="text-[#A8A398] mt-0.5">
                {tierInfo.abo.seats_included} Freikarte{tierInfo.abo.seats_included !== 1 ? "n" : ""} pro Vorstellung
                {tierInfo.abo.snack_discount_pct > 0 && <> · {tierInfo.abo.snack_discount_pct}% auf Snacks</>}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-[#A8A398]">Zwischensumme</span>
            <span className="font-mono">{subtotal.toFixed(2)} €</span>
          </div>
          {tierDiscount > 0 && (
            <div className="flex items-center justify-between text-[#D4AF37]">
              <span>Club-Rabatt</span>
              <span className="font-mono">− {tierDiscount.toFixed(2)} €</span>
            </div>
          )}
          {aboFreeSavings > 0 && (
            <div className="flex items-center justify-between text-[#8FBC8F]" data-testid="pos-abo-free-savings">
              <span>Abo · {Math.min(tierInfo?.abo?.seats_included || 0, selectedSeats.length)} Freikarte(n)</span>
              <span className="font-mono">− {aboFreeSavings.toFixed(2)} €</span>
            </div>
          )}
          {aboSnackDiscount > 0 && (
            <div className="flex items-center justify-between text-[#8FBC8F]" data-testid="pos-abo-snack-discount">
              <span>Abo · Snack {tierInfo?.abo?.snack_discount_pct}%</span>
              <span className="font-mono">− {aboSnackDiscount.toFixed(2)} €</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="text-[#A8A398]">Rabatt €</span>
            <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)}
              className="w-24 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-right font-mono focus:border-[#D4AF37] outline-none" />
          </div>
          {Number(familyInfo?.discount || 0) > 0 && (
            <div className="flex items-center justify-between gap-3 border border-[#EEDC82]/30 bg-[#EEDC82]/5 rounded px-2 py-1" data-testid="pos-family-hint">
              <span className="text-[#EEDC82] text-xs">🎉 Familientarif ({familyInfo.percent}% ab {familyInfo.min_tickets}+)</span>
              <span className="font-mono text-[#EEDC82]">− {Number(familyInfo.discount).toFixed(2)} €</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="text-[#A8A398] flex items-center gap-1">
              Trinkgeld €
              <span className="text-[9px] text-[#575148] uppercase tracking-widest">(nicht steuerbar)</span>
            </span>
            <div className="flex items-center gap-1">
              {[1, 2, 5].map((q) => (
                <button key={q} type="button" onClick={() => setTip((t) => (Number(t) || 0) + q)}
                  data-testid={`pos-tip-quick-${q}`}
                  className="text-[10px] px-2 py-1 border border-[#EEDC82]/40 text-[#EEDC82] rounded hover:bg-[#EEDC82]/10">
                  +{q}
                </button>
              ))}
              <input type="number" step="0.10" value={tip} onChange={(e) => setTip(e.target.value)}
                data-testid="pos-tip-input" min="0"
                className="w-20 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-right font-mono focus:border-[#EEDC82] outline-none" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[#A8A398]">Gutscheincode</span>
            <div className="flex items-center gap-1">
              <input value={voucher} onChange={(e) => { setVoucher(e.target.value.toUpperCase()); setVoucherInfo(null); }}
                data-testid="pos-voucher-input" placeholder="CODE"
                className="w-28 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-right font-mono uppercase focus:border-[#D4AF37] outline-none" />
              <button type="button" onClick={() => setScannerOpen(true)} data-testid="pos-voucher-scan-btn"
                className="border border-[#D4AF37]/40 text-[#D4AF37] rounded px-2 py-1 hover:bg-[#D4AF37] hover:text-black" aria-label="QR scannen">
                <ScanLine size={14} />
              </button>
            </div>
          </div>
          {voucherInfo && (
            <div className="rounded border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-3 py-2 text-xs" data-testid="pos-voucher-info">
              <div className="text-[#D4AF37] uppercase tracking-widest">
                {voucherInfo.kind === "ticket_pack" ? `${voucherInfo.ticket_count}er-Karte · ${voucherInfo.ticket_uses_remaining} übrig` : "Geld-Gutschein"}
              </div>
              <div className="text-[#F5F0E6] mt-0.5 font-mono">
                Guthaben: {(voucherInfo.remaining_value || 0).toFixed(2)} €
                {voucherInfo.recipient_name ? ` · ${voucherInfo.recipient_name}` : ""}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-[#1A1A1A] pt-3">
            <span className="font-heading text-lg">Gesamt</span>
            <span className="font-heading text-2xl text-[#D4AF37]" data-testid="pos-total">{total.toFixed(2)} €</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5 mt-3">
            {PAY.map((p) => (
              <button key={p.v} onClick={() => { setPayment(p.v); if (splitMode) setSplitMode(false); }}
                className={`text-[11px] uppercase tracking-widest px-2 py-2 rounded border ${payment === p.v && !splitMode ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398]"}`}>
                {p.l}
              </button>
            ))}
          </div>
          {/* Split-Payment (mehrere Zahlarten) */}
          <div className="mt-3 border border-[#1A1A1A] rounded p-2">
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A8A398] cursor-pointer">
              <input type="checkbox" checked={splitMode} onChange={(e) => setSplitMode(e.target.checked)} data-testid="pos-split-toggle" />
              Split-Payment (Kunde teilt Zahlung auf)
            </label>
            {splitMode && (
              <div className="mt-2 space-y-1.5" data-testid="pos-split-lines">
                {splitPayments.map((sp, i) => (
                  <div key={i} className="flex gap-1.5 items-center">
                    <select value={sp.method} onChange={(e) => updateSplitLine(i, { method: e.target.value })}
                      className="bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-xs">
                      <option value="bar">Bar</option>
                      <option value="karte">Karte</option>
                      <option value="karte_stripe">Karte (Stripe)</option>
                      <option value="gutschein">Gutschein</option>
                      <option value="mitglied">Mitglied</option>
                    </select>
                    <input type="number" step="0.01" value={sp.amount} onChange={(e) => updateSplitLine(i, { amount: e.target.value })}
                      className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-xs font-mono text-right" placeholder="0.00 €" />
                    {splitPayments.length > 2 && (
                      <button onClick={() => removeSplitLine(i)} className="text-xs text-[#EE9CA1] hover:text-[#EE5C5C] px-1">×</button>
                    )}
                  </div>
                ))}
                <div className="flex gap-2 items-center text-[10px] pt-1">
                  <button onClick={addSplitLine} className="text-[#D4AF37] uppercase tracking-widest hover:underline">+ Zahlart</button>
                  <div className={`ml-auto font-mono ${splitBalanced ? "text-[#D4AF37]" : "text-[#EE9CA1]"}`} data-testid="pos-split-sum">
                    {splitSum.toFixed(2)} € / {total.toFixed(2)} € {splitBalanced ? "✓" : ""}
                  </div>
                </div>
              </div>
            )}
          </div>
          <button data-testid="pos-checkout-btn" onClick={sell} disabled={busy || (subtotal === 0)}
            className="w-full mt-4 py-3 bg-[#D4AF37] hover:bg-[#EEDC82] text-black uppercase tracking-widest text-sm rounded transitions-safe disabled:opacity-40 flex items-center justify-center gap-2">
            <CheckCircle2 size={16} /> {busy ? "Buchung …" : "Abschließen"}
          </button>
        </div>
      </aside>
      {scannerOpen && (
        <VoucherScanner
          onDetected={async (code) => {
            setVoucher(code);
            setScannerOpen(false);
            try {
              const { data } = await api.get(`/vouchers/lookup/${encodeURIComponent(code)}`);
              setVoucherInfo(data);
              if (data.status !== "aktiv") toast.warning(`Gutschein ${data.status}`);
              else toast.success(`Guthaben ${data.remaining_value.toFixed(2)} €`);
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gutschein nicht gefunden");
              setVoucherInfo(null);
            }
          }}
          onClose={() => setScannerOpen(false)}
        />
      )}
      {receipt && <ReceiptDialog receipt={receipt} onClose={() => setReceipt(null)} />}
      {stripeModal && <StripePayModal modal={stripeModal} busy={busy} onFinalize={finalizeStripe} onCancel={() => setStripeModal(null)} />}
      {fullscreenPreview && showtime && (
        <FullscreenSeatPreview
          showtime={showtime}
          selected={selectedSeats}
          ticketType={ticketType}
          priceEach={priceEach}
          products={cartProducts}
          subtotal={subtotal}
          tierDiscount={tierDiscount}
          discount={parseFloat(discount) || 0}
          total={total}
          payment={payment}
          guest={guestName}
          onToggle={toggleSeat}
          onClose={() => setFullscreenPreview(false)}
        />
      )}
    </div>
  );
}

function FullscreenSeatPreview({ showtime, selected, ticketType, priceEach, products, subtotal, tierDiscount, discount, total, payment, guest, onToggle, onClose }) {
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const seatSum = priceEach * selected.length;
  const productSum = products.reduce((s, p) => s + p.quantity * p.unit_price, 0);
  const payMap = { bar: "Bar", karte: "Karte", karte_stripe: "Karte · Stripe", gutschein: "Gutschein", einladung: "Einladung", frei: "Kostenlos", mitglied: "Mitgliedskarte" };
  const dateEU = (d) => { if (!d) return ""; const p = d.split("-"); return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : d; };
  return (
    <div className="fixed inset-0 z-50 pos-display-bg overflow-auto" data-testid="pos-fullscreen-preview">
      <div className="min-h-screen flex flex-col">
        <div className="px-8 py-4 border-b border-[#1A1A1A] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">The Movie House · Kinokasse</div>
            <div className="font-heading text-3xl">Sitzplan-Vorschau</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#575148]">ESC zum Schließen</div>
            <button onClick={onClose} data-testid="pos-fullscreen-close-btn"
              className="border border-[#D4AF37]/60 text-[#D4AF37] rounded px-4 py-2 hover:bg-[#D4AF37]/10 inline-flex items-center gap-2">
              <X size={16} /> Schließen
            </button>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-1 xl:grid-cols-[380px_1fr_360px] gap-6 p-6">
          <aside className="space-y-4">
            {showtime.movie?.poster_url && (
              <div className="rounded-xl overflow-hidden border border-[#D4AF37]/30 shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
                <img src={showtime.movie.poster_url} alt="" className="w-full block" />
              </div>
            )}
            <div className="card-lux p-4 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Vorstellung</div>
              <div className="font-heading text-2xl leading-tight">{showtime.movie?.title_de || showtime.movie?.title_original}</div>
              <div className="text-base text-[#A8A398]">{dateEU(showtime.date)} · {showtime.start_time} · {showtime.hall}</div>
              {showtime.movie?.duration_min && <div className="text-sm text-[#A8A398]">Dauer: {showtime.movie.duration_min} min{showtime.movie?.age_rating ? ` · FSK ${showtime.movie.age_rating}` : ""}</div>}
            </div>
          </aside>
          <div className="card-lux p-6">
            <div className="seatmap-display">
              <SeatMap rows={showtime.seat_plan?.rows || []} seatTypes={showtime.seat_plan?.seat_types || []}
                seatStatus={showtime.seat_status || {}} selected={selected} onToggle={onToggle} curve={6}
                decorations={showtime.seat_plan?.decorations || []} backgroundUrl={showtime.seat_plan?.background_url || ""} backgroundOpacity={showtime.seat_plan?.background_opacity ?? 0.35}
                layers={showtime.seat_plan?.layers || []} freeMode={!!showtime.seat_plan?.free_mode} />
            </div>
          </div>
          <aside className="space-y-3">
            <div className="card-lux p-5">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3">Ihre Bestellung</div>
              <div className="max-h-[60vh] overflow-y-auto space-y-1">
                {selected.map((id) => (
                  <div key={id} className="flex justify-between items-baseline border-b border-[#1A1A1A]/60 py-2">
                    <div>
                      <div className="text-lg">Ticket · {ticketType}</div>
                      <div className="text-xs text-[#A8A398] font-mono">Platz {id}</div>
                    </div>
                    <div className="font-mono text-lg">{priceEach.toFixed(2)} €</div>
                  </div>
                ))}
                {products.map((p) => (
                  <div key={p.product_id} className="flex justify-between items-baseline border-b border-[#1A1A1A]/60 py-2">
                    <div>
                      <div className="text-lg">{p.name}</div>
                      <div className="text-xs text-[#A8A398] font-mono">{p.quantity}× {p.unit_price.toFixed(2)} €</div>
                    </div>
                    <div className="font-mono text-lg">{(p.quantity * p.unit_price).toFixed(2)} €</div>
                  </div>
                ))}
                {!selected.length && !products.length && (
                  <div className="text-[#575148] text-center py-8">Noch nichts ausgewählt</div>
                )}
              </div>
              {(seatSum + productSum) > 0 && (
                <div className="mt-4 pt-3 border-t border-[#1A1A1A] space-y-2 text-base">
                  <div className="flex justify-between text-[#A8A398]"><span>Zwischensumme</span><span className="font-mono">{subtotal.toFixed(2)} €</span></div>
                  {tierDiscount > 0 && <div className="flex justify-between text-[#D4AF37]"><span>Club-Rabatt</span><span className="font-mono">− {tierDiscount.toFixed(2)} €</span></div>}
                  {discount > 0 && <div className="flex justify-between text-[#D4AF37]"><span>Rabatt</span><span className="font-mono">− {discount.toFixed(2)} €</span></div>}
                  <div className="flex justify-between items-baseline pt-3 border-t border-[#1A1A1A]/60">
                    <span className="font-heading text-xl">Gesamt</span>
                    <span className="font-heading text-3xl text-[#D4AF37]">{total.toFixed(2)} €</span>
                  </div>
                  <div className="text-center text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">Zahlart: <span className="text-[#EEDC82]">{payMap[payment] || payment}</span></div>
                </div>
              )}
            </div>
            {guest && guest !== "Kunde" && (
              <div className="card-lux p-3 text-center">
                <div className="text-[9px] uppercase tracking-[0.4em] text-[#A8A398]">Kunde</div>
                <div className="font-heading text-lg mt-1">{guest}</div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function StripePayModal({ modal, busy, onFinalize, onCancel }) {
  const [status, setStatus] = useState({ paid: false, checking: true, error: null });
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${modal.sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus({ paid: true, checking: false, error: null });
          return;
        }
        if (data.payment_status === "failed") {
          setStatus({ paid: false, checking: false, error: "Zahlung fehlgeschlagen" });
          return;
        }
        if (attempts > 120) { // 6 min timeout
          setStatus({ paid: false, checking: false, error: "Timeout — Kunde hat nicht bezahlt" });
          return;
        }
        setTimeout(poll, 3000);
      } catch (e) {
        if (!cancelled) setTimeout(poll, 3000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [modal.sessionId]);
  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pos-stripe-modal">
      <div className="relative w-full max-w-md bg-[#0F0D08] border border-[#D4AF37]/40 rounded-lg p-8 text-center">
        <button onClick={onCancel} className="absolute top-3 right-3 text-[#A8A398] hover:text-[#D4AF37]"><X size={20} /></button>
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] flex items-center justify-center gap-2"><CreditCard size={14} /> Kartenzahlung · Stripe</div>
        <div className="font-heading text-2xl sm:text-3xl md:text-4xl mt-3 text-[#D4AF37]">{modal.amount.toFixed(2)} €</div>
        <div className="text-xs text-[#A8A398] mt-1">{modal.description}</div>
        {!status.paid && (
          <>
            <div className="mt-6 bg-white p-4 rounded inline-block">
              <QRCodeCanvas value={modal.url} size={220} />
            </div>
            <div className="mt-4 text-xs text-[#F5F0E6] leading-relaxed">
              Kunde scannt den QR-Code mit dem Handy<br />und zahlt sicher via Stripe.
            </div>
            <div className="mt-5 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]">
              <Loader2 size={12} className="animate-spin" /> Warte auf Zahlung
            </div>
            {status.error && <div className="mt-3 text-xs text-[#EE9CA1]">{status.error}</div>}
          </>
        )}
        {status.paid && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#D4AF37]/15 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mt-6">
              <CheckCircle2 size={40} className="text-[#D4AF37]" />
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-[0.4em] text-[#8FBC8F]">Bezahlt</div>
            <button onClick={onFinalize} disabled={busy} data-testid="pos-stripe-finalize"
              className="mt-6 w-full py-3 bg-[#D4AF37] hover:bg-[#EEDC82] text-black text-[11px] uppercase tracking-[0.4em] rounded disabled:opacity-40">
              {busy ? "Buche …" : "Bon erstellen"}
            </button>
          </>
        )}
        {!status.paid && (
          <button onClick={onCancel} className="mt-6 text-[10px] uppercase tracking-[0.4em] text-[#A8A398] underline">
            Zahlung abbrechen
          </button>
        )}
      </div>
    </div>
  );
}

function ReceiptDialog({ receipt, onClose }) {
  const { order, tickets, showtime, cartSnapshot: c } = receipt;
  const printRef = useRef(null);

  const doPrint = () => {
    const win = window.open("", "receipt", "width=380,height=680");
    if (!win) return toast.error("Popup blockiert — bitte erlauben");
    win.document.write(`<!doctype html><html><head><title>Beleg ${order.order_number}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; padding: 12px; width: 300px; color: #000; }
  h1 { font-size: 15px; text-align: center; margin: 0 0 4px; letter-spacing: 3px; }
  .sub { text-align: center; font-size: 10px; color: #666; margin-bottom: 12px; }
  .row { display: flex; justify-content: space-between; margin: 3px 0; font-size: 12px; }
  .sep { border-top: 1px dashed #000; margin: 8px 0; }
  .total { font-size: 15px; font-weight: bold; }
  .foot { text-align: center; font-size: 10px; margin-top: 14px; color: #555; }
</style></head><body>${printRef.current?.innerHTML || ""}</body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 150);
  };

  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  const dt = new Date(order.created_at || Date.now());
  const dateStr = dt.toLocaleDateString("de-DE") + " " + dt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

  const payLabel = { bar: "Bar", karte: "Karte", gutschein: "Gutschein", einladung: "Einladung", frei: "Kostenlos", mitglied: "Mitgliedskarte" }[c.payment] || c.payment;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="pos-receipt-dialog">
      <div className="relative bg-[#0F0D08] border border-[#D4AF37]/40 rounded-lg p-6 max-w-md w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-[#F5F0E6]/60 hover:text-[#D4AF37]"><X size={20} /></button>

        <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] flex items-center gap-2">
          <Receipt size={14} /> Kassenbeleg
        </div>
        <div className="font-heading text-3xl mt-1">Verkauf abgeschlossen</div>
        <div className="text-xs text-[#A8A398] mt-1">Nr. {order.order_number} · {dateStr}</div>

        {/* Preview des Belegs — auch für Druckversion via printRef */}
        <div className="mt-5 bg-[#F5F0E6] text-black rounded p-4 font-mono text-xs shadow-inner" ref={printRef}
             style={{ lineHeight: 1.5 }}>
          <h1 style={{ textAlign: "center", fontSize: 15, letterSpacing: 3, margin: "0 0 4px" }}>{c.cinema.toUpperCase()}</h1>
          <div className="sub" style={{ textAlign: "center", fontSize: 10, color: "#666" }}>Beleg · {order.order_number}</div>
          <div className="sub" style={{ textAlign: "center", fontSize: 10, color: "#666", marginBottom: 10 }}>{dateStr} · Gast: {c.guest}</div>
          {showtime && (
            <div className="sub" style={{ textAlign: "center", fontSize: 10, color: "#666", marginBottom: 10 }}>
              {showtime.movie?.title_de || "Film"} · {showtime.date} {showtime.start_time} · {showtime.hall}
            </div>
          )}
          <div className="sep" style={{ borderTop: "1px dashed #000", margin: "8px 0" }} />
          {c.seats.map((s, i) => (
            <div className="row" key={`s${i}`} style={{ display: "flex", justifyContent: "space-between", margin: "3px 0" }}>
              <span>Ticket {s.type} · {s.id}</span>
              <span>{s.price.toFixed(2)} €</span>
            </div>
          ))}
          {c.products.map((p) => (
            <div className="row" key={p.product_id} style={{ display: "flex", justifyContent: "space-between", margin: "3px 0" }}>
              <span>{p.quantity}× {p.name}</span>
              <span>{(p.quantity * p.unit_price).toFixed(2)} €</span>
            </div>
          ))}
          <div className="sep" style={{ borderTop: "1px dashed #000", margin: "8px 0" }} />
          <div className="row" style={{ display: "flex", justifyContent: "space-between", margin: "3px 0" }}><span>Zwischensumme</span><span>{c.subtotal.toFixed(2)} €</span></div>
          {c.tierDiscount > 0 && <div className="row" style={{ display: "flex", justifyContent: "space-between", margin: "3px 0" }}><span>Club-Rabatt</span><span>− {c.tierDiscount.toFixed(2)} €</span></div>}
          {c.discount > 0 && <div className="row" style={{ display: "flex", justifyContent: "space-between", margin: "3px 0" }}><span>Rabatt</span><span>− {c.discount.toFixed(2)} €</span></div>}
          {c.voucher && c.voucherDeducted > 0 && (
            <div className="row" style={{ display: "flex", justifyContent: "space-between", margin: "3px 0" }}>
              <span>Gutschein {c.voucher.code}{c.voucher.kind === "ticket_pack" ? " (10er)" : ""}</span>
              <span>− {c.voucherDeducted.toFixed(2)} €</span>
            </div>
          )}
          {order?.tip > 0 && (
            <div className="row" style={{ display: "flex", justifyContent: "space-between", margin: "3px 0", color: "#8a6d00" }}>
              <span>+ Trinkgeld</span><span>+ {Number(order.tip).toFixed(2)} €</span>
            </div>
          )}
          <div className="sep" style={{ borderTop: "1px dashed #000", margin: "8px 0" }} />
          <div className="row total" style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: "bold", margin: "3px 0" }}><span>GESAMT</span><span>{c.total.toFixed(2)} €</span></div>
          <div className="row" style={{ display: "flex", justifyContent: "space-between", margin: "3px 0" }}><span>Zahlart</span><span>{payLabel}</span></div>
          {c.payment === "split" && Array.isArray(c.payments) && c.payments.length > 0 && (
            <div style={{ marginTop: 4, marginBottom: 4, paddingLeft: 8, borderLeft: "2px solid #999" }}>
              {c.payments.map((p, i) => (
                <div key={i} className="row" style={{ display: "flex", justifyContent: "space-between", margin: "2px 0", fontSize: 11 }}>
                  <span>· {({bar:"Bar",karte:"Karte",karte_stripe:"Karte·Stripe",gutschein:"Gutschein",ueberweisung:"Überweisung",mitglied:"Mitglied"})[p.method] || p.method}</span>
                  <span>{Number(p.amount).toFixed(2)} €</span>
                </div>
              ))}
            </div>
          )}
          <div className="foot" style={{ textAlign: "center", fontSize: 10, marginTop: 14, color: "#555" }}>
            Danke für deinen Besuch!<br />
            {tickets.length > 0 && `${tickets.length} Ticket${tickets.length > 1 ? "s" : ""} als PDF verschickt.`}
          </div>
        </div>

        <div className="mt-5 flex gap-2 flex-wrap">
          <button onClick={doPrint} data-testid="pos-receipt-print-btn"
            className="flex-1 min-w-[140px] bg-[#D4AF37] text-black px-4 py-3 rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#EEDC82] flex items-center justify-center gap-2">
            <Printer size={14} /> Browser-Druck
          </button>
          <button onClick={async () => {
              try { const { data } = await api.post(`/pos/receipt-print/${order.id}`); toast.success(`Gedruckt (${data.printer})`); }
              catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Drucker nicht erreichbar"); }
            }} data-testid="pos-receipt-bondrucker-btn"
            className="px-4 py-3 border border-[#D4AF37] text-[#D4AF37] rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#D4AF37]/10 flex items-center gap-2">
            <Printer size={14} /> Bondrucker
          </button>
          <button onClick={async () => {
              try { const { data } = await api.post("/pos/drawer-open", { printer_type: "receipt" }); toast.success(`Schublade offen (${data.printer})`); }
              catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kein Drucker konfiguriert"); }
            }} data-testid="pos-receipt-drawer-btn"
            className="px-4 py-3 border border-[#D4AF37]/40 text-[#F5F0E6] rounded text-[11px] uppercase tracking-[0.4em] hover:border-[#D4AF37] flex items-center gap-2">
            <Receipt size={14} /> Kassenlade
          </button>
          {tickets.length > 0 && (
            <>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${tickets[0].id}/pdf`} target="_blank" rel="noreferrer"
                onClick={(e) => { e.preventDefault(); openPdf(`/tickets/${tickets[0].id}/pdf`); }}
                className="px-4 py-3 border border-[#D4AF37]/40 text-[#D4AF37] rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#D4AF37]/10 flex items-center gap-2" data-testid="pos-receipt-ticket-btn">
                Ticket 1 · PDF
              </a>
              {tickets.length > 1 && (
                <button onClick={() => tickets.forEach((t) => openPdf(`/tickets/${t.id}/pdf`))}
                  data-testid="pos-receipt-all-tickets-btn"
                  className="px-4 py-3 border border-[#D4AF37]/40 text-[#D4AF37] rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#D4AF37]/10 flex items-center gap-2">
                  Alle {tickets.length} Tickets · PDF
                </button>
              )}
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${tickets[0].id}/pdf`} target="_blank" rel="noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  tickets.forEach((t, i) => setTimeout(() => openPdf(`/tickets/${t.id}/pdf`), i * 250));
                }}
                data-testid="pos-receipt-a4-tickets-preview-btn"
                title={`Alle ${tickets.length} A4-Tickets als PDF öffnen`}
                className="px-4 py-3 border border-[#EEDC82]/40 text-[#EEDC82] rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#EEDC82]/10 flex items-center gap-2">
                <FileText size={14} /> A4-Tickets · PDF
              </a>
              <button onClick={async () => {
                  try {
                    let n = 0;
                    for (const t of tickets) {
                      try { await api.post(`/tickets/${t.id}/print-office`); n++; } catch { /* skip */ }
                    }
                    if (n > 0) toast.success(`${n} Ticket${n > 1 ? "s" : ""} auf A4 gedruckt`);
                    else {
                      toast.info("Bürodrucker nicht erreichbar — PDFs werden geöffnet");
                      tickets.forEach((t, i) => setTimeout(() => openPdf(`/tickets/${t.id}/pdf`), i * 250));
                    }
                  } catch (err) {
                    toast.info("PDFs werden geöffnet");
                    tickets.forEach((t, i) => setTimeout(() => openPdf(`/tickets/${t.id}/pdf`), i * 250));
                  }
                }} data-testid="pos-receipt-print-tickets-btn"
                className="px-4 py-3 border border-[#8FBC8F]/40 text-[#8FBC8F] rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#8FBC8F]/10 flex items-center gap-2">
                <Printer size={14} /> A4-Tickets · Drucken
              </button>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/receipt.pdf`} target="_blank" rel="noreferrer"
                onClick={(e) => { e.preventDefault(); openPdf(`/orders/${order.id}/receipt.pdf`); }}
                className="px-4 py-3 border border-[#A8A398]/40 text-[#A8A398] rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#A8A398]/10 flex items-center gap-2" data-testid="pos-receipt-bon-pdf-btn">
                Bon-PDF
              </a>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/invoice.pdf`} target="_blank" rel="noreferrer"
                onClick={(e) => { e.preventDefault(); openPdf(`/orders/${order.id}/invoice.pdf`); }}
                className="px-4 py-3 border border-[#D4AF37]/40 text-[#D4AF37] rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#D4AF37]/10 flex items-center gap-2" data-testid="pos-receipt-invoice-pdf-btn">
                Rechnung
              </a>
              <button onClick={async () => {
                  try { const { data } = await api.post(`/orders/${order.id}/print-office-invoice`); toast.success(`Rechnung gedruckt (${data.printer})`); }
                  catch (e) {
                    toast.info("Bürodrucker nicht erreichbar — PDF wird geöffnet");
                    openPdf(`/orders/${order.id}/invoice.pdf`);
                  }
                }} data-testid="pos-receipt-print-invoice-btn"
                className="px-4 py-3 border border-[#8FBC8F]/40 text-[#8FBC8F] rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#8FBC8F]/10 flex items-center gap-2">
                <Printer size={14} /> Rechnung drucken
              </button>
            </>
          )}
          {tickets.length === 0 && order.id && (
            <>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/receipt.pdf`} target="_blank" rel="noreferrer"
                onClick={(e) => { e.preventDefault(); openPdf(`/orders/${order.id}/receipt.pdf`); }}
                className="px-4 py-3 border border-[#A8A398]/40 text-[#A8A398] rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#A8A398]/10 flex items-center gap-2">
                Bon-PDF
              </a>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/invoice.pdf`} target="_blank" rel="noreferrer"
                onClick={(e) => { e.preventDefault(); openPdf(`/orders/${order.id}/invoice.pdf`); }}
                className="px-4 py-3 border border-[#D4AF37]/40 text-[#D4AF37] rounded text-[11px] uppercase tracking-[0.4em] hover:bg-[#D4AF37]/10 flex items-center gap-2">
                Rechnung
              </a>
            </>
          )}
          <button onClick={onClose} data-testid="pos-receipt-close-btn"
            className="px-4 py-3 border border-[#2A2A2A] text-[#A8A398] rounded text-[11px] uppercase tracking-[0.4em] hover:border-[#575148]">
            Weiter
          </button>
        </div>
      </div>
    </div>
  );
}

function VoucherScanner({ onDetected, onClose }) {
  const scannerRef = useRef(null);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const q = new Html5Qrcode("voucher-qr-region");
        scannerRef.current = q;
        await q.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 240 },
          (decoded) => {
            if (!active) return;
            active = false;
            // Falls URL, letzten Path-Teil verwenden. Ansonsten dekodierten Text als Code
            let code = decoded;
            try { const u = new URL(decoded); code = u.pathname.split("/").filter(Boolean).pop() || decoded; } catch { /* not url */ }
            onDetected(code.toUpperCase());
          },
          () => {},
        );
      } catch {
        toast.error("Kamera nicht verfügbar");
      }
    })();
    return () => {
      active = false;
      const q = scannerRef.current;
      if (q) { q.stop().then(() => q.clear()).catch(() => {}); }
    };
  }, [onDetected]);
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={onClose} data-testid="pos-voucher-scanner">
      <div className="relative bg-[#0F0D08] border border-[#D4AF37]/40 rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-[#F5F0E6]/60 hover:text-[#D4AF37]"><X size={20} /></button>
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] flex items-center gap-2 mb-3">
          <Camera size={14} /> Gutschein-QR scannen
        </div>
        <div id="voucher-qr-region" className="w-full aspect-square bg-black rounded overflow-hidden" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#575148] text-center mt-3">
          Halte den QR-Code des Gäste-PDFs vor die Kamera
        </div>
      </div>
    </div>
  );
}

function CashChangeModal({ cash, onCancel, onConfirm }) {
  const [given, setGiven] = useState(cash.given || cash.total);
  const change = Math.max(0, Number(given) - Number(cash.total));
  const enough = Number(given) >= Number(cash.total);
  const quick = [5, 10, 20, 50, 100];
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onCancel(); if (e.key === "Enter" && enough) onConfirm(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [enough, onCancel, onConfirm]);
  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[80] flex items-center justify-center p-4" onClick={onCancel} data-testid="pos-cash-modal">
      <div className="w-full max-w-lg bg-[#0A0A0A] border border-[#D4AF37]/60 rounded-2xl overflow-hidden shadow-[0_40px_120px_rgba(0,0,0,0.9)]" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#1A1A1A]">
          <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Wechselgeld-Rechner</div>
          <div className="font-heading text-2xl mt-1">Bar-Zahlung</div>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-baseline justify-between border-b border-[#1A1A1A]/60 pb-3">
            <div className="text-sm text-[#A8A398] uppercase tracking-widest">Zu zahlen</div>
            <div className="font-heading text-3xl text-[#F5F0E6] font-mono">{Number(cash.total).toFixed(2)} €</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Vom Kunden erhalten</label>
            <input type="number" step="0.01" value={given} autoFocus onFocus={(e) => e.target.select()}
              onChange={(e) => setGiven(e.target.value)} data-testid="pos-cash-given"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded-lg px-4 py-4 text-3xl font-mono text-center focus:border-[#D4AF37] outline-none" />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {quick.map((n) => (
                <button key={n} onClick={() => setGiven(String(n))}
                  className="text-xs px-3 py-1.5 rounded border border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]">
                  {n} €
                </button>
              ))}
              <button onClick={() => setGiven(String(Math.ceil(Number(cash.total))))}
                className="text-xs px-3 py-1.5 rounded border border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]">
                Aufrunden {Math.ceil(Number(cash.total))} €
              </button>
              <button onClick={() => setGiven(String(cash.total))}
                className="text-xs px-3 py-1.5 rounded border border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]">
                Passend
              </button>
            </div>
          </div>
          <div className={`rounded-xl p-4 border ${enough ? "border-[#D4AF37]/50 bg-[#D4AF37]/10" : "border-[#7A1921]/40 bg-[#7A1921]/10"}`}>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">
              {enough ? "Rückgeld für Kunden" : "Betrag reicht nicht"}
            </div>
            <div className={`font-heading text-5xl mt-2 font-mono ${enough ? "text-[#D4AF37]" : "text-[#EE9CA1]"}`} data-testid="pos-cash-change">
              {enough ? change.toFixed(2) : (Number(given) - Number(cash.total)).toFixed(2)} €
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-[#1A1A1A] flex gap-2 justify-end">
          <button onClick={onCancel} data-testid="pos-cash-cancel"
            className="px-5 py-2.5 border border-[#2A2A2A] rounded text-sm text-[#A8A398] hover:text-[#EE9CA1] hover:border-[#7A1921]">
            Abbrechen
          </button>
          <button onClick={onConfirm} disabled={!enough} data-testid="pos-cash-confirm"
            className="px-6 py-2.5 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded text-sm uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed">
            Kassieren · {enough ? `${change.toFixed(2)} € Rückgeld` : "Betrag zu niedrig"}
          </button>
        </div>
      </div>
    </div>
  );
}
